// libs
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';

// context
import { useCartContext } from 'shared/components/CartProvider/context';
import { Coupon, COUPON_TYPE } from 'shared/layouts/Coupons/types';

// components
import Image from 'shared/components/ImageV2/index';
import CouponDeltaCommunication from 'shared/layouts/Coupons/components/DeltaCommunication/DeltaCommunication';
import CopyCode from 'shared/ui-components/Coupon/Components/CopyCode';
import GradientTimer from 'shared/components/GradientTimer';

// icons
import { useStoreIcon, IconName } from 'shared/icons';
import Logo from 'shared/components/Logo';

// constants
import { TEXTS } from 'shared/layouts/Coupons/constants';
import {
  COUPON_EXPIRED,
  OFFER_EXPIRED,
  EXPIRY_THRESHOLD_TIME,
  VIEW_PRODUCTS,
  COUPON_ACTION_TYPE,
  ICON_SIZES,
} from './constants';
import { LOGO_TYPE, TAB_INDEX } from 'shared/constants';
import { COUPON_CLICK_LOCATION_TYPE } from 'shared/layouts/Coupons/constants';

// analytics
import {
  onCouponShopMoreClickedCart,
  onViewProductsClicked,
} from 'shared/layouts/Coupons/analytics';

// styles
import {
  Container,
  SaveText,
  InfoContainer,
  InfoContent,
  IconContainer,
  Content,
  Title,
  Description,
  ActionContainer,
  CouponCodeContainer,
  CouponCode,
  AppliedText,
  Button,
  DetailsButton,
  DetailText,
  IconContainerWrapper,
  LockIcon,
  CouponsExpiryTimer,
  ExpiredText,
  CopyContainer,
  SaleExpText,
  SaleExpWrapper,
} from './styles';

// helpers
import { toXDecimal } from 'shared/layouts/Coupons/helper';
import { DEVICE_TYPE, getDeviceType } from 'shared/helpers/app';
import { goToProduct as goToOfferListing } from 'shared/helpers/messaging';

// hook
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// types
import { Theme } from '@nykaa/ui-components/themes/types';
import useTimer from 'shared/components/Timer/useTimer';

// defs
interface CouponRowProps {
  coupon: Coupon;
  className?: string;
  disabled: boolean;
  isApplied: boolean;
  backgroundImage: string;
  onDetailsClicked: (coupon: Coupon, disabled: boolean, showViewProduct?: boolean) => void;
  onApplyClicked?: (couponCode: string) => void;
  onRemoveClicked?: (couponCode: string) => void;
  onCopyClicked: (couponCode: string, coupon: Coupon) => void;
  actionContainer?: (isExpired: boolean) => React.ReactNode;
  actionButtonType?: string;
  type?: COUPON_TYPE;
  hasScrollSnap?: boolean;
  logoHeight?: number;
  logoWidth?: number;
  subtitleTrimLength?: number;
  disableApplyBtn?: boolean;
  showSaveUpto?: React.ReactNode | null;
  couponExpiryEnabled?: boolean;
  isOffer?: boolean;
  isEligibleForDeltaCommunication?: boolean;
  isPDP?: boolean;
  showViewProduct?: boolean;
  isBottomSheet?: boolean;
  isInStock?: boolean;
}

const EMPTY_FUNC = () => {
  /** DO NOTHING */
};

// constants
const EXPIRE_TEXT = 'Expires in';

const PLP_REDIRECT_URL = `/op`;

function CouponRow({
  coupon,
  className = '',
  isApplied,
  disabled,
  backgroundImage,
  onDetailsClicked,
  onApplyClicked,
  onRemoveClicked = () => { },
  onCopyClicked = EMPTY_FUNC,
  actionContainer,
  actionButtonType = 'apply',
  type = COUPON_TYPE.COUPON,
  hasScrollSnap = false,
  logoHeight = 54,
  logoWidth = 54,
  subtitleTrimLength = 45,
  disableApplyBtn,
  showSaveUpto = null,
  couponExpiryEnabled = false,
  isOffer = false,
  isEligibleForDeltaCommunication = false,
  isPDP = false,
  showViewProduct = false,
  isBottomSheet = false,
  isInStock = false
}: CouponRowProps) {
  // Get store-aware icon components
  const CircleTick = useStoreIcon(IconName.CIRCLE_TICK);
  const ChevronDownSmallIcon = useStoreIcon(IconName.CHEVRON_DOWN_SMALL);
  const CircleLockIcon = useStoreIcon(IconName.CIRCLE_LOCK);
  const CopyIcon = useStoreIcon(IconName.COPY);

  const theme: Theme = useTheme();

  const { currencySymbol } = useCartContext();

  const {
    title,
    subTitle,
    discount,
    imageUrl = '',
    couponCode,
    expiryEpoch = 0,
    viewProductsFlag = false,
    deltaTextModified = '',
    status = '',
    couponDetails,
    deltaAmount,
    isViewProducts = false,
  } = coupon;

  const history = useHistory();
  const isDesktop = getDeviceType() === DEVICE_TYPE.DESKTOP;
  const timeDifference = expiryEpoch * 1000 - new Date().getTime();
  const showCouponExpiry = timeDifference <= EXPIRY_THRESHOLD_TIME && timeDifference > 0;
  const [isExpired, setIsExpired] = useState(false);
  const detailsRef = useRef(null);
  const isApplyButtonDisabled = disableApplyBtn || disabled || isExpired;
  const {
    enabled: saleExpEnabled,
    saleCommunication,
    theme: saleExpTheme,
  } = useRemoteConfig('saleExperience') || {};

  const saleExperienceEnabled = saleExpEnabled && saleCommunication?.enabled && saleCommunication?.pdp?.enabled;
  const offerSaleExp = saleExperienceEnabled && type === COUPON_TYPE.OFFER;
  const couponSaleExp = saleExperienceEnabled && type === COUPON_TYPE.COUPON;

  const onClicked = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (actionButtonType === COUPON_ACTION_TYPE.APPLY) {
      // if coupon is already applied than call remove coupon
      if (isApplied) {
        onRemoveClicked(couponCode);
        return;
      }

      // if coupon is not applied call apply coupon
      onApplyClicked && onApplyClicked(coupon.couponCode);
    } else if (actionButtonType === COUPON_ACTION_TYPE.VIEW_PRODUCT) {
      // handle view products click
      const url = `${PLP_REDIRECT_URL}/${coupon.offerId}?coupon_offer_id=${coupon.offerId}&couponId=${coupon.couponCode}`;
      if (isDesktop) {
        goToOfferListing(url);
      } else {
        history.push(url);
      }
      onCouponShopMoreClickedCart(coupon, isEligibleForDeltaCommunication, false);
      onViewProductsClicked({
        couponType: 'coupon',
        couponClickType: COUPON_CLICK_LOCATION_TYPE.COUPONS_PAGE_WIDGET_DELTA,
      });
    } else {
      onCopyClicked(coupon?.couponCode || couponCode, coupon);
    }
  };

  const onViewProductsKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onClicked(event);
    }
  };

  const handleCopyClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onCopyClicked(coupon?.couponDetails?.couponCode || couponCode, coupon);
  };

  const onCopyCodeKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      onCopyClicked(coupon?.couponDetails?.couponCode || couponCode, coupon);
    }
  };

  useEffect(() => {
    if (!detailsRef.current) return;
  });

  const getSubTitle = useCallback(
    (desc: string) => {
      const length = desc.length;

      if (length > subtitleTrimLength) {
        return desc.substring(0, subtitleTrimLength) + '...    ';
      }

      return desc + ' ';
    },
    [subtitleTrimLength]
  );

  const isOfferExpired = (isExpired || timeDifference <= 0) && expiryEpoch > 0;

  const renderCouponExpiry = () => {
    return !isOfferExpired && showCouponExpiry ? (
      <CouponsExpiryTimer expiryDate={expiryEpoch} setIsExpired={setIsExpired} isOffer={isOffer} />
    ) : (
      isOfferExpired && (
        <ExpiredText className="coupon-expiry">
          {isOffer ? OFFER_EXPIRED : COUPON_EXPIRED}
        </ExpiredText>
      )
    );
  };

  const { timeRemaining = '' } = useTimer({
    initTime: expiryEpoch,
    setIsExpired,
    showDays: false
  });

  const shouldShowDeltaCommunication =
    isEligibleForDeltaCommunication && isPDP
      ? true
      : isEligibleForDeltaCommunication && showViewProduct
        ? true
        : false;

  const enableViewProduct = actionButtonType === COUPON_ACTION_TYPE.VIEW_PRODUCT;
  return (
    <Container
      className={className}
      scrollSnap={hasScrollSnap}
      couponDisabled={disabled}
      isExpired={isOfferExpired}
    >
      {couponExpiryEnabled && !disabled && renderCouponExpiry()}
      {!disabled && discount > 0 && (
        <SaveText isApplied={isApplied}>
          {isApplied && <CircleTick className="appliedTick" />}
          <span className="saveText">
            {isApplied ? `${TEXTS.YOU_SAVED} ` : `Save `}
            <span data-test="currency">{currencySymbol}</span>
            {toXDecimal(discount)}
          </span>
        </SaveText>
      )}
      {disableApplyBtn && (
        <LockIcon>
          <CircleLockIcon color={theme.colors.secondary} size={ICON_SIZES.LOCK_ICON_SIZE} />
        </LockIcon>
      )}
      {showSaveUpto && showSaveUpto}
      <InfoContainer
        isOffer={isOffer}
        disabled={disabled}
        tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
        onClick={
          isOfferExpired
            ? () => { }
            : () => onDetailsClicked(coupon, isApplyButtonDisabled, showViewProduct)
        }
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            isOfferExpired
              ? () => { }
              : onDetailsClicked(coupon, isApplyButtonDisabled, showViewProduct);
          }
        }}
        backgroundImage={backgroundImage}
        hideBottomBorder={!shouldShowDeltaCommunication && type === COUPON_TYPE.COUPON}
        className={`infoContainer ${showCouponExpiry || timeDifference <= 0 ? 'hasTimer' : ''}`}
        isBottomSheet={isBottomSheet}
      >
        {/* {couponSaleExp && isInStock && !isBottomSheet && (
          <SaleExpWrapper bg={saleExpTheme.bgColor}>
            <SaleExpText gradientColors={saleExpTheme.textGradient}>
              {saleCommunication.pdp.stickyPriceTag}
            </SaleExpText>
          </SaleExpWrapper>
        )}
        {offerSaleExp && isInStock && !isBottomSheet && (
          <SaleExpWrapper bg={saleExpTheme.bgColor}>
            {EXPIRE_TEXT}{' '}
            <SaleExpText gradientColors={saleExpTheme.textGradient} bold={true}>
              {timeRemaining}
            </SaleExpText>
          </SaleExpWrapper>
        )} */}
        <InfoContent disabled={disabled} className="infoContent">
          <IconContainerWrapper>
            <IconContainer className="iconContainer">
              {imageUrl === '' || imageUrl === null ? (
                type === COUPON_TYPE.COUPON ? (
                  <Logo type={LOGO_TYPE.COUPONS} />
                ) : (
                  <Logo type={LOGO_TYPE.OFFERS} />
                )
              ) : (
                <Image
                  src={imageUrl}
                  width={logoWidth}
                  height={logoHeight}
                  alt={getSubTitle(subTitle)}
                  className="coupon-background-image"
                />
              )}
            </IconContainer>
          </IconContainerWrapper>
          <Content>
            <Title className="couponTitle">{title}</Title>
            <Description ref={detailsRef} className="couponDescription">
              <p>{subTitle && getSubTitle(subTitle)}</p>
              <DetailsButton aria-hidden>
                <DetailText>{TEXTS.DETAILS}</DetailText>
              </DetailsButton>
              {!isOffer && shouldShowDeltaCommunication && (
                <CopyCode
                  copyCode={coupon?.couponDetails?.couponCode || couponCode}
                  handleCopyClick={handleCopyClick}
                />
              )}
            </Description>
          </Content>
        </InfoContent>
      </InfoContainer>
      {shouldShowDeltaCommunication && (
        <CouponDeltaCommunication
          deltaModifiedText={deltaTextModified}
          isDetailsView={false}
          status={status}
          couponDetails={{ ...couponDetails, couponCode }}
          offerId={coupon.offerId}
          isOffer={isOffer}
          deltaAmount={deltaAmount}
          couponData={coupon}
          isPDP={isPDP}
          viewProductsFlag={!isPDP ? viewProductsFlag : isViewProducts}
        />
      )}
      {actionContainer && !shouldShowDeltaCommunication ? (
        <>
          <div className="action-overlay" />
          {actionContainer(isOfferExpired)}
        </>
      ) : (
        !shouldShowDeltaCommunication && (
          <ActionContainer isApplied={isApplied} className="action-container">
            <CouponCodeContainer isViewProduct={enableViewProduct}>
              <CouponCode>{coupon.couponCode}</CouponCode>

              {enableViewProduct && (
                <CopyContainer onKeyDown={onCopyCodeKeyDown} tabIndex={TAB_INDEX.ACTION_INVISIBILITY} aria-label={TEXTS.COPY_CODE} onClick={handleCopyClick}>
                  <CopyIcon size={ICON_SIZES.COPY_ICON_SIZE} />
                </CopyContainer>
              )}
              {isApplied && <AppliedText>{TEXTS.APPLIED}</AppliedText>}
            </CouponCodeContainer>
            {disableApplyBtn && (
              <LockIcon>
                <CircleLockIcon color={theme.colors.secondary} size={ICON_SIZES.LOCK_ICON_SIZE} />
              </LockIcon>
            )}
            {enableViewProduct && (
              <Button onClick={onClicked} onKeyDown={onViewProductsKeyDown} disabled={!viewProductsFlag} isApplied={false}>
                {VIEW_PRODUCTS}
              </Button>
            )}
            {actionButtonType === COUPON_ACTION_TYPE.APPLY && (
              <Button
                disabled={isApplyButtonDisabled || isExpired}
                isApplied={isApplied}
                onClick={isOfferExpired ? () => { } : onClicked}
              >
                {isApplied ? TEXTS.REMOVE : TEXTS.APPLY}
              </Button>
            )}
            {actionButtonType === COUPON_ACTION_TYPE.COPY && (
              <Button
                disabled={disabled || isExpired}
                isApplied={isApplied}
                onClick={isOfferExpired ? () => { } : onClicked}
              >
                {TEXTS.COPY}
              </Button>
            )}
          </ActionContainer>
        )
      )}
    </Container>
  );
}

export default memo(CouponRow);
